import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';
import { useApproveChallengePost, useChallengePostById, useRegenerateChallengePost, useUpdateChallengePost } from '../apis/knackchallenge';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getEnabledPlatforms } from '../utils/platformUtils';
import { validateInstagramImageUrl } from '../utils/instagramValidation';

// your custom query hook
// replace with correct path
// Axios instance

const ChallengeEditPost = () => {
    const search = useLocation().search;
    const ContentId = new URLSearchParams(search).get("ContentId");
    const [content, setContent] = useState('');
    const [imageUrl, setImageUrl] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [enabledPlatforms, setEnabledPlatforms] = useState([]);
    const [platformsLoading, setPlatformsLoading] = useState(true);
    const options = { ALLOWED_TAGS: [] }
    const fileInputRef = useRef(null);

    console.log('ChallengeEditPost ID:', useParams()); // Debugging line

    // React Query: Fetch the challenge post
    const { data: post, isLoading, isError } = useChallengePostById(ContentId);

    // React Query: Update and approve
    const { mutate: updatePost, isPending: isSaving } = useUpdateChallengePost();
    const { mutateAsync: approvePost, isPending: isApproving } = useApproveChallengePost();

    const { mutate: regeneratePost, isPending: isRegenerating ,data:regeneratingData } = useRegenerateChallengePost();

    // Fetch enabled platforms on component mount
    useEffect(() => {
        const fetchPlatforms = async () => {
            try {
                const platforms = await getEnabledPlatforms('knackChallenges');
                setEnabledPlatforms(platforms.map(p => p.code));
                setPlatformsLoading(false);
            } catch (error) {
                console.error('Error fetching enabled platforms:', error);
                setPlatformsLoading(false);
            }
        };
        fetchPlatforms();
    }, []);

    // Set content from fetched post
    useEffect(() => {
        if (post?.ChallengePostContext) {
            setContent(post.ChallengePostContext);
            setImageUrl(post.Image)
        }
    }, [post]);
  function convert(htmlString, options) {
        const clean = DOMPurify.sanitize(htmlString, options);
        
        return clean;
    }
    // Save content
    const handleSave = async () => {
        // Check if Instagram is enabled
        const instagramEnabled = enabledPlatforms.includes('instagram') || enabledPlatforms.includes('Instagram');

        // Validate image if Instagram is enabled
        if (instagramEnabled) {
            let imageToValidate = null;
            
            if (selectedFile) {
                // User uploaded a new image - validate the file
                try {
                    const imageUrl = URL.createObjectURL(selectedFile);
                    const validationResult = await validateInstagramImageUrl(imageUrl);
                    URL.revokeObjectURL(imageUrl); // Clean up
                    
                    if (!validationResult.isValid) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Image Validation Failed',
                            html: validationResult.error || 'Image does not meet Instagram requirements',
                            confirmButtonText: 'OK'
                        });
                        return;
                    }
                } catch (validationError) {
                    console.error('Image validation error:', validationError);
                    // Continue with update - backend will do final validation
                }
            } else if (imageUrl && imageUrl.trim() !== '' && imageUrl !== 'null') {
                // User kept existing image - validate the URL
                try {
                    const fullImageUrl = imageUrl.startsWith('http') 
                        ? imageUrl 
                        : `${process.env.REACT_APP_API_BASE_URL}/${imageUrl}`;
                    const validationResult = await validateInstagramImageUrl(fullImageUrl);
                    
                    if (!validationResult.isValid) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Image Validation Failed',
                            html: validationResult.error || 'Image does not meet Instagram requirements',
                            confirmButtonText: 'OK'
                        });
                        return;
                    }
                } catch (validationError) {
                    console.error('Image validation error:', validationError);
                    // Continue with update - backend will do final validation
                }
            } else {
                // No image provided
                Swal.fire({
                    icon: 'error',
                    title: 'Image Required',
                    text: 'Image is required for Instagram posts. Please add an image before updating.',
                    confirmButtonText: 'OK'
                });
                return;
            }
        }

        const formData = new FormData();
        formData.append('ChallengePostID', ContentId);
        console.log("48",convert(content, options))
        formData.append('ChallengePostContext', convert(content, options));
        
        if (selectedFile) {
            // ✅ User uploaded a new image
            formData.append('image', selectedFile);
        } else if (imageUrl === "") {
            // ✅ User removed image and didn't upload a new one
            formData.append('image', null);
        } else {
            // ✅ User kept existing image and didn't upload new one
            formData.append('image', imageUrl);
        }
        
        updatePost(
            {
                formData, ChallengePostID: ContentId 
            },
            {
                onSuccess: () => Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Post updated successfully',
                showConfirmButton: false,
                timer: 3000
                }),
                onError: (error) => {
                    console.error('Update error:', error);
                    const errorData = error.response?.data || error.data;
                    
                    // Check for image validation errors
                    if (errorData?.errorType === 'image_validation' || errorData?.errorType === 'image_required') {
                        Swal.fire({
                            icon: 'error',
                            title: 'Image Validation Failed',
                            html: `
                                <div style="text-align: left;">
                                    <p style="margin-bottom: 15px;"><strong>${errorData.message || 'Image does not meet Instagram requirements.'}</strong></p>
                                    ${errorData.errorDetails ? `
                                        <p style="margin-bottom: 10px; color: #666;">
                                            <strong>Details:</strong><br/>
                                            ${errorData.errorDetails.aspectRatio ? `Aspect Ratio: ${errorData.errorDetails.aspectRatio.toFixed(2)}:1<br/>` : ''}
                                            ${errorData.errorDetails.dimensions ? `Dimensions: ${errorData.errorDetails.dimensions}<br/>` : ''}
                                        </p>
                                    ` : ''}
                                    <p style="margin-bottom: 20px; color: #666;">Instagram requires images with aspect ratios between 0.8:1 and 1.91:1 (approximately 4:5 portrait to 1.91:1 landscape).</p>
                                    <p style="color: #666;">Please select a different image with a valid aspect ratio.</p>
                                </div>
                            `,
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#2e3192',
                        });
                    } else {
                        Swal.fire({
                            position: 'center',
                            icon: 'error',
                            title: 'Failed to save post',
                            text: errorData?.message || error.message || 'An error occurred while updating the post',
                            showConfirmButton: true,
                            confirmButtonColor: '#2e3192',
                        });
                    }
                }
            }
        );
    };

    // Approve content
    const handleApprove = async () => {
        // Check if Instagram is enabled
        const instagramEnabled = enabledPlatforms.includes('instagram') || enabledPlatforms.includes('Instagram');

        // Validate image if Instagram is enabled
        if (instagramEnabled) {
            let imageToValidate = null;
            
            if (selectedFile) {
                // User uploaded a new image - validate the file
                try {
                    const imageUrl = URL.createObjectURL(selectedFile);
                    const validationResult = await validateInstagramImageUrl(imageUrl);
                    URL.revokeObjectURL(imageUrl); // Clean up
                    
                    if (!validationResult.isValid) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Image Validation Failed',
                            html: validationResult.error || 'Image does not meet Instagram requirements',
                            confirmButtonText: 'OK'
                        });
                        return;
                    }
                } catch (validationError) {
                    console.error('Image validation error:', validationError);
                    // Continue with approve - backend will do final validation
                }
            } else if (imageUrl && imageUrl.trim() !== '' && imageUrl !== 'null') {
                // User kept existing image - validate the URL
                try {
                    const fullImageUrl = imageUrl.startsWith('http') 
                        ? imageUrl 
                        : `${process.env.REACT_APP_API_BASE_URL}/${imageUrl}`;
                    const validationResult = await validateInstagramImageUrl(fullImageUrl);
                    
                    if (!validationResult.isValid) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Image Validation Failed',
                            html: validationResult.error || 'Image does not meet Instagram requirements',
                            confirmButtonText: 'OK'
                        });
                        return;
                    }
                } catch (validationError) {
                    console.error('Image validation error:', validationError);
                    // Continue with approve - backend will do final validation
                }
            } else {
                // No image provided
                Swal.fire({
                    icon: 'error',
                    title: 'Image Required',
                    text: 'Image is required for Instagram posts. Please add an image before approving.',
                    confirmButtonText: 'OK'
                });
                return;
            }
        }

        approvePost(
            {
                challengePostID: ContentId,
                status: 2, // 2 = approved
            },
            {
                onSuccess: () => Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Post approved successfully',
                showConfirmButton: false,
                timer: 3000
                }),
                onError: (error) => {
                    console.error('Approve error:', error);
                    const errorData = error.response?.data || error.data;
                    
                    // Check for image validation errors
                    if (errorData?.errorType === 'image_validation' || errorData?.errorType === 'image_required') {
                        Swal.fire({
                            icon: 'error',
                            title: 'Image Validation Failed',
                            html: `
                                <div style="text-align: left;">
                                    <p style="margin-bottom: 15px;"><strong>${errorData.message || 'Image does not meet Instagram requirements.'}</strong></p>
                                    ${errorData.errorDetails ? `
                                        <p style="margin-bottom: 10px; color: #666;">
                                            <strong>Details:</strong><br/>
                                            ${errorData.errorDetails.aspectRatio ? `Aspect Ratio: ${errorData.errorDetails.aspectRatio.toFixed(2)}:1<br/>` : ''}
                                            ${errorData.errorDetails.dimensions ? `Dimensions: ${errorData.errorDetails.dimensions}<br/>` : ''}
                                        </p>
                                    ` : ''}
                                    <p style="margin-bottom: 20px; color: #666;">Instagram requires images with aspect ratios between 0.8:1 and 1.91:1 (approximately 4:5 portrait to 1.91:1 landscape).</p>
                                    <p style="color: #666;">Please select a different image with a valid aspect ratio.</p>
                                </div>
                            `,
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#2e3192',
                        });
                    } else {
                        Swal.fire({
                            position: 'center',
                            icon: 'error',
                            title: 'Failed to approve post',
                            text: errorData?.message || error.message || 'An error occurred while approving the post',
                            showConfirmButton: true,
                            confirmButtonColor: '#2e3192',
                        });
                    }
                }
            }
        );
    };

    // Regenerate content
    const handleRegenerate = () => {
        regeneratePost({
            challengeType: post.ChallengeTypeID,
            challengeTopic: post.ChallengeTopic,
            subtopicTitle: post.SubtopicTitle,
        }, {
            onSuccess: (data) => {
                setContent(data.regeneratedContent);
            },
            onError: (err) => {
                console.error('Failed to regenerate content:', err);
                Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Regeneration failed',
                showConfirmButton: false,
                timer: 3000
                });

            },
        });
    };

    if (isLoading) return <p>Loading post...</p>;
    if (isError || !post) return <p>Error loading post.</p>;

    return (
        <div className="max-w-3xl mx-auto p-4">
        <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>Edit Challenge Post</h5>
        <hr/>
 {imageUrl && (
          <div style={{ textAlign: "center", marginBottom: "10px" }}>
            <img src={`${process.env.REACT_APP_API_BASE_URL}/${imageUrl}`} alt="Post" style={{ maxWidth: "200px", borderRadius: "5px", display: "block", margin: "0 auto" }} />
            <br />
            <button 
              type="button" 
              onClick={() => {
                setImageUrl("");
                setSelectedFile(null);
                setImagePreview(null);
                setVideoPreview(null);
              }}
              style={{
                background: '#2e3192',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                marginTop: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1c1f6d';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2e3192';
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
              Remove Image
            </button>
          </div>
        )}
            {/* <ReactQuill value={content} onChange={setContent} /> */}
            <p>
  <strong style={{marginRight:10,color:'#000',marginTop:10}}>
  <span>{post?.SubtopicTitle}</span></strong>
</p>
       <textarea
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}

                                        className="mb-6"
                                          style={{
                                    fontSize: '15px',
                                    borderRadius: '8px',
                                    border: '1px solid #cfeafb',
                                    background: '#FFFFFF',
                                    height: '400px',
                                    width: "100%",
                                    padding: '0px 20px',
                                    fontSize: '16px',
                                }}/>
             {!imageUrl && (
        <div  style={{ marginBottom: "10px" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              
              // If no file selected (user cancelled), clear everything
              if (!file) {
                setSelectedFile(null);
                setImagePreview(null);
                setVideoPreview(null);
                return;
              }
              
              const maxSize = 50 * 1024 * 1024; // 50 MB in bytes
              
              if (file.size > maxSize) {
                Swal.fire({
                  icon: 'error',
                  title: 'File Size Exceeded',
                  text: 'The file size should not exceed 50 MB.',
                  showConfirmButton: true,
                });
                setSelectedFile(null);
                setImagePreview(null);
                setVideoPreview(null);
                return;
              }
              
              setSelectedFile(file);
              
              // Create preview for image files
              if (file.type && file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  setImagePreview(e.target.result);
                  setVideoPreview(null); // Clear video preview when image is selected
                };
                reader.readAsDataURL(file);
              } else if (file.type && file.type.startsWith('video/')) {
                // Create preview for video files
                const reader = new FileReader();
                reader.onload = (e) => {
                  setVideoPreview(e.target.result);
                  setImagePreview(null); // Clear image preview when video is selected
                };
                reader.readAsDataURL(file);
              } else {
                setImagePreview(null);
                setVideoPreview(null);
              }
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Upload Image or Video"
            style={{
              fontSize: '16px',
              borderRadius: '50%',
              border: 'none',
              background: '#2e3192',
              padding: '12px',
              color: 'white',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              minWidth: '48px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1a1d5c';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#2e3192';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <FontAwesomeIcon icon={faUpload} />
          </button>
          {selectedFile && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ marginBottom: '10px', fontWeight: 'bold', color: '#2e3192' }}>
                Selected: {(selectedFile.type && selectedFile.type.startsWith('video/')) ? 'Uploaded Video' : 'Uploaded Image'}
              </p>
              {imagePreview && (
                <div>
                  <img
                    src={imagePreview}
                    alt="Uploaded"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid #2e3192"
                    }}
                  />
                </div>
              )}
              {videoPreview && (
                <div>
                  <video
                    src={videoPreview}
                    controls
                    style={{
                      width: "200px",
                      maxHeight: "200px",
                      borderRadius: "8px",
                      border: "2px solid #2e3192"
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </div>
          )}
        </div>
)}
     <div style={{ display: 'flex', marginTop: '1rem' }}>
  <button
    onClick={handleSave}
    disabled={isSaving}
    className="btn btn-primary"
    style={{ marginRight: '1rem' }}
  >
    {isSaving ? 'Updating...' : 'Update'}
  </button>
  <button
    onClick={handleApprove}
    disabled={isApproving}
    className="btn btn-primary"
    style={{ marginRight: '1rem' }}
  >
    {isApproving ? 'Approving...' : 'Approve'}
  </button>
  <button
    onClick={handleRegenerate}
    className="btn btn-primary"
  >
   Craft
  </button>
</div>


        </div>
    );
};

export default ChallengeEditPost;
